import { defineNuxtPlugin, mobileBearerToken, useLayoutMode } from '#imports'
import { graphqlClientInjectionKey } from '~/composables/graphql'
import { setResponseHeader } from 'h3'
import type { H3Event } from 'h3'
import { useRequestHeaders, useRouter } from '#app'
import { AwesomeGraphQLClient } from 'awesome-graphql-client'
import { type TypedDocumentNode } from '@graphql-typed-document-node/core'
import { print } from 'graphql'
import { getNewTokenFromCapacitor } from '~/composables/mobile/capacitor-bridge'
import {
    bootstrapPublicCsrf,
    getLaravelPublicEndpoint
} from '~/server/utils/public-csrf'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function mobileFetch(url: string, options?: any) {
    const router = useRouter()

    // Perform original fetch
    let response = await fetch(url, options)

    // Intercept 401 Unauthorized
    if (response.status === 401) {
        // Pause execution and get new token from Capacitor
        let newToken = ''
        try {
            newToken = await getNewTokenFromCapacitor()
        } catch {
            router.replace('/')
            mobileBearerToken.value = null
            alert(
                'Sorry, the app is having trouble finding your login credentials'
            )
            throw new Error()
        }
        mobileBearerToken.value = newToken

        // Clone the original request options but swap in the new token
        const newOptions = { ...options }
        newOptions.headers = {
            ...newOptions.headers,
            Authorization: 'Bearer ' + newToken
        }

        // Retry the original fetch with the new token
        response = await fetch(url, newOptions)

        // If the retry still returns 401, something went wrong
        if (response.status === 401) {
            router.replace('/')
            mobileBearerToken.value = null
            alert(
                'Sorry, we are having difficulty refreshing your login credentials'
            )
            throw new Error()
        }
    }

    return response
}

async function serverFetchWithContentSecurityPolicyHeader(
    event: H3Event,
    url: string,
    options?: RequestInit
) {
    const bootstrap = await bootstrapPublicCsrf(event)
    const headers = new Headers(options?.headers)

    // SSR GraphQL posts use the same CSRF contract as browser posts, including
    // the Laravel session cookie created by the bootstrap endpoint.
    if (bootstrap.csrfToken !== null) {
        headers.set('X-CSRF-TOKEN', bootstrap.csrfToken)
    }
    if (bootstrap.cookieHeader !== '') {
        headers.set('cookie', bootstrap.cookieHeader)
    }

    const response = await fetch(url, {
        ...options,
        headers
    })

    for (const headerName of [
        'content-security-policy',
        'content-security-policy-report-only',
        'referrer-policy',
        'strict-transport-security',
        'x-content-type-options'
    ]) {
        const securityHeaderValue = response.headers.get(headerName)
        if (securityHeaderValue !== null) {
            // Laravel owns CPX security headers, but browsers enforce them on the top-level
            // HTML response from Nuxt.
            setResponseHeader(event, headerName, securityHeaderValue)
        }
    }

    return response
}

export default defineNuxtPlugin(nuxt => {
    const { isMobileApp } = useLayoutMode()

    let client: AwesomeGraphQLClient<TypedDocumentNode>
    if (process.server && nuxt.ssrContext) {
        const headers = useRequestHeaders()
        const event = nuxt.ssrContext.event
        client = new AwesomeGraphQLClient({
            endpoint: getLaravelPublicEndpoint(event, '/graphql-public'),
            fetchOptions: { headers },
            formatQuery: (query: TypedDocumentNode) => print(query),
            fetch: (url: string, options?: RequestInit) =>
                serverFetchWithContentSecurityPolicyHeader(event, url, options)
        })
    } else {
        client = new AwesomeGraphQLClient({
            endpoint: '/graphql-public',
            formatQuery: (query: TypedDocumentNode) => print(query),
            fetch: isMobileApp.value ? mobileFetch : undefined
        })
    }

    nuxt.vueApp.provide(graphqlClientInjectionKey, client)
})
